import { clsx, type ClassValue } from 'clsx';
import { format, parseISO } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import type { OfferFilterableItem, StatusCounts } from './api/types';

/**
 * Combines multiple class names using clsx and merges Tailwind classes using tailwind-merge
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Enforces a maximum number of decimal places for numeric input
 * Removes all non-digit characters except decimal point and limits decimal places
 */
export const enforceDecimalLimit = (value: string, maxDecimals: number): string => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const parts = cleaned.split('.');
  if (parts.length <= 1) return cleaned;
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('').slice(0, maxDecimals);
  }
  return parts[0] + '.' + parts[1].slice(0, maxDecimals);
};

/**
 * Enforces integer-only input by stripping all non-digit characters
 * Used for fields that should only accept whole numbers
 */
export const enforceIntegerOnly = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

/**
 * Formats a number as currency with enhanced options support
 */
export const formatCurrency = (
  amount: number | string,
  currencyCode: string = 'USD',
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
  }).format(numericAmount);
};

/**
 * Formats a stored monetary value (e.g. a benefit amount) showing cents only
 * when present: whole values render without decimals ($1,500), fractional
 * values with exactly two ($1,500.50) — never a lone trailing digit ($1,500.5).
 * Unlike formatCurrency(), which rounds to whole dollars, this preserves cents.
 */
export const formatMonetaryValue = (
  amount: number | string,
  currencyCode: string = 'USD'
): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  const fractionDigits = Number.isInteger(numericAmount) ? 0 : 2;

  return formatCurrency(numericAmount, currencyCode, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

/**
 * Gets currency symbol for display
 */
export const getCurrencySymbol = (currencyCode: string = 'USD'): string => {
  switch (currencyCode.toUpperCase()) {
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    case 'JPY':
    case 'CNY':
      return '¥';
    case 'INR':
      return '₹';
    case 'KRW':
      return '₩';
    case 'RUB':
      return '₽';
    case 'USD':
    default:
      return '$';
  }
};

/**
 * Format bonus type for display
 */
export const formatBonusType = (type: string): string => {
  if (type === 'recurring') return 'Recurring Bonus';
  if (type === 'one_time') return 'One-time Bonus';
  return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
};

/**
 * Format payment frequency for display
 */
export const formatPaymentFrequency = (frequency: string): string => {
  const frequencies: Record<string, string> = {
    weekly: 'Paid Weekly',
    biweekly: 'Paid Biweekly',
    semi_monthly: 'Paid Twice a Month',
    monthly: 'Paid Monthly',
    quarterly: 'Quarterly',
    biannual: 'Biannual',
    annual: 'Annual',
  };

  return frequencies[frequency] || frequency.charAt(0).toUpperCase() + frequency.slice(1);
};

/**
 * Format bonus amount for display with currency handling
 */
export const formatBonusAmount = (
  amount: string | number,
  amountType: string = 'flat',
  currency: string = 'USD'
): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (amountType === 'percentage') {
    return `${numericAmount}%`;
  } else {
    return formatCurrency(numericAmount, currency);
  }
};

/**
 * Format equity type for display
 */
export const formatEquityType = (equityType: string): string => {
  switch (equityType) {
    case 'stock_options':
      return 'Stock Options';
    case 'rsus':
      return 'RSUs (Restricted Stock Units)';
    default:
      return equityType || 'Stock Options';
  }
};

/**
 * Get frequency display suffix for monetary values
 */
export const getFrequencyDisplay = (frequency?: string): string => {
  switch (frequency) {
    case 'monthly':
      return '/month';
    case 'quarterly':
      return '/quarter';
    case 'annually':
      return '/year';
    case 'first_year':
      return '/first year';
    case 'one_time':
    default:
      return '';
  }
};

/**
 * Formats a date to a human-readable string
 * Preserves the original date without timezone adjustment
 */
export const formatDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    // Parse the date parts directly from the string to avoid timezone issues
    const [year, month, day] = date.split('T')[0].split('-').map(Number);

    // Create a date object with the specified year, month (0-indexed), and day
    const dateObj = new Date(year, month - 1, day);

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // If it's already a Date object, use it directly
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Formats a date string to abbreviated month + year (e.g., "Feb 2025")
 * Preserves the original date without timezone adjustment
 */
export const formatShortDate = (dateString: string): string => {
  const [year, month] = dateString.split('T')[0].split('-').map(Number);
  const dateObj = new Date(year, month - 1, 1);
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Formats a date to a human-readable string with timezone
 * Preserves the original date without timezone adjustment
 */
export const formatDateWithTimezone = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    // Get the timezone abbreviation
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formattedDate = format(date, 'MMMM d, yyyy h:mm a');
    return `${formattedDate} (${timeZone})`;
  } catch (error) {
    console.warn('Error formatting date:', error);
    return '--'; // Fall back to the original string if there's an error
  }
};

/**
 * Gets the CSRF token from cookies
 */
export const getCsrfToken = (): string => {
  const name = 'csrftoken';
  let cookieValue = '';
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

/**
 * Downloads a PDF file from a base64 string
 *
 * @param pdfData - Object containing PDF data and metadata
 * @param pdfData.pdf_file - Base64 encoded PDF file data (with or without data URI prefix)
 * @param pdfData.pdf_filename - Filename to use for the downloaded file
 * @param pdfData.pdf_mimetype - MIME type of the PDF file
 * @returns void
 */
export const downloadPdfFromBase64 = (pdfData: {
  pdf_file?: string;
  pdf_filename?: string;
  pdf_mimetype?: string;
}): void => {
  if (!pdfData.pdf_file) return;

  // Remove the data URI prefix to get just the base64 data
  const base64Data = pdfData.pdf_file.includes(',')
    ? pdfData.pdf_file.split(',')[1]
    : pdfData.pdf_file;

  if (!base64Data) return;

  // Create a Blob from the base64 data
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: pdfData.pdf_mimetype || 'application/pdf' });

  // Create a temporary URL and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = pdfData.pdf_filename || 'benefit-document.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Calculate relative time from a date
 */
export const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }
  return '';
};

/**
 * Filter offers by status
 * Uses OfferFilterableItem type from @lib/api/types
 */
export const filterOffersByStatus = <T extends OfferFilterableItem>(
  offers: T[],
  statusFilter: string
): T[] => {
  if (statusFilter === 'all-statuses') {
    return offers;
  }

  if (statusFilter === 'pending') {
    return offers.filter(offer => {
      const status = offer.status.toLowerCase();
      return status === 'unpublished' || status === 'published' || status === 'viewed';
    });
  }

  return offers.filter(offer => offer.status.toLowerCase() === statusFilter.toLowerCase());
};

/**
 * Filter offers by search query
 */
export const filterOffersBySearch = <T extends OfferFilterableItem>(
  offers: T[],
  searchQuery: string
): T[] => {
  if (!searchQuery.trim()) {
    return offers;
  }

  const query = searchQuery.toLowerCase().trim();

  return offers.filter(offer => {
    // Search in candidate name
    const candidateName = offer.candidate.name.toLowerCase();
    if (candidateName.includes(query)) return true;

    // Search in company name if available
    if (offer.company) {
      const companyName = offer.company.name.toLowerCase();
      if (companyName.includes(query)) return true;
    }

    // Search in department if available
    const department = offer.department?.toLowerCase() || '';
    if (department.includes(query)) return true;

    // Search in status
    const status = offer.status.toLowerCase();
    if (status.includes(query)) return true;

    // Search in salary (as string)
    const salaryStr = offer.salary.toString();
    if (salaryStr.includes(query)) return true;

    return false;
  });
};

/**
 * Count offers by status
 * Uses StatusCounts interface from @lib/api/types
 */
export const countOffersByStatus = <T extends OfferFilterableItem>(offers: T[]): StatusCounts => {
  const counts: StatusCounts = {
    all: offers.length,
    accepted: 0,
    pending: 0,
    declined: 0,
    expired: 0,
    reneged: 0,
    rescinded: 0,
  };

  offers.forEach(offer => {
    const status = offer.status.toLowerCase();
    if (status === 'accepted') counts.accepted++;
    else if (status === 'published' || status === 'viewed' || status === 'unpublished')
      counts.pending++;
    else if (status === 'declined') counts.declined++;
    else if (status === 'expired') counts.expired++;
    else if (status === 'reneged') counts.reneged++;
    else if (status === 'rescinded') counts.rescinded++;
  });

  return counts;
};

// Lighten a hex color toward white — used for a classy, brand-led celebration
// palette (company brand + paper white + a lighter brand tint) instead of rainbow.
export const lightenHex = (hex: string, amount = 0.35): string => {
  const parts = hex.replace('#', '').match(/.{2}/g);
  if (!parts || parts.length < 3) return hex;
  const mix = (channel: string): string => {
    const value = parseInt(channel, 16);
    return Math.round(value + (255 - value) * amount)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${mix(parts[0])}${mix(parts[1])}${mix(parts[2])}`;
};

/**
 * Initials for an avatar fallback: first letters of the first and last words.
 * Returns '' for empty/whitespace names so callers can fall back to an icon.
 */
export const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = [...words[0]][0] ?? '';
  const last = words.length > 1 ? ([...words[words.length - 1]][0] ?? '') : '';
  return (first + last).toUpperCase();
};

/**
 * Luminance-aware foreground for text sitting ON a brand-colored fill
 * (feeds `--offer-brand-ink`). Light brands (e.g. the demo gold) get dark
 * ink; dark brands get near-white. Values are DESIGN.md's Ink/Paper text
 * tokens. Invalid hex falls back to near-white, matching the CSS default.
 */
export const getContrastInk = (hex: string): string => {
  const value = hex.trim().replace('#', '');
  const expanded = value.length === 3 ? [...value].map(c => c + c).join('') : value;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return '#F4F5F7';
  const [r, g, b] = [0, 2, 4].map(i => parseInt(expanded.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.35 ? '#141A1F' : '#F4F5F7';
};
